import { Link } from 'react-router-dom';

import type { ConversationTurnDto } from '../../conversations/types/conversation';
import type { MachineDocument } from '../../documents/types/document';
import type { PinnedEvidenceDto } from '../types/investigation';

export function PinnedEvidencePanel({
  machineId,
  evidence,
  turns,
  documents,
  sourceConversationId,
  onClose,
}: {
  machineId: string;
  evidence: PinnedEvidenceDto;
  turns: ConversationTurnDto[];
  documents: MachineDocument[];
  sourceConversationId?: string | null;
  onClose: () => void;
}) {
  const resolved = resolveEvidence(evidence, turns);
  const documentById = new Map(documents.map((document) => [document.id, document]));
  const document = resolved.documentId ? documentById.get(resolved.documentId) : undefined;
  const title = resolved.label || document?.filename || humanize(evidence.artifact_type);

  return (
    <aside className="r42-evidence-panel" aria-label="Pinned investigation evidence">
      <header className="r42-evidence-panel__head">
        <div>
          <span className="r42-eyebrow">Evidence</span>
          <h2>Pinned investigation evidence</h2>
        </div>
        <button className="r42-icon-button" onClick={onClose} aria-label="Close pinned evidence">
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
      </header>

      <div className="r42-evidence-panel__body">
        <div className="r42-pinned-evidence-summary">
          <span className="r42-eyebrow">Investigation context</span>
          <strong>{humanize(evidence.artifact_type)}</strong>
          <p>{evidence.note || 'Pinned to this investigation.'}</p>
        </div>

        <article className="r42-evidence-card r42-pinned-evidence-card">
          <div className="r42-evidence-card__top">
            <span>Pinned</span>
            <small>{humanize(evidence.artifact_type)}</small>
          </div>
          <strong>{title}</strong>
          {document ? <span className="r42-evidence-source">Source: {document.filename}</span> : null}
          {resolved.excerpt ? <blockquote>{resolved.excerpt}</blockquote> : (
            <div className="r42-pinned-evidence-unresolved">
              No preserved excerpt is available for this pinned artifact in the current investigation conversation. The artifact reference remains part of the investigation context.
            </div>
          )}
          {locatorText(resolved.sourceLocator) ? <p>{locatorText(resolved.sourceLocator)}</p> : null}
          <p className="r42-pinned-evidence-reference"><strong>Reference:</strong> {evidence.artifact_id}</p>
          {document ? (
            <Link to={`/machines/${machineId}/documents/${document.id}`}>
              Open document <i className="bi bi-arrow-up-right" aria-hidden="true" />
            </Link>
          ) : null}
        </article>

        <div className="r42-pinned-evidence-provenance">
          <span className="r42-eyebrow">Provenance</span>
          <p>Pinned {formatPinnedAt(evidence.pinned_at)}.</p>
          {sourceConversationId ? (
            <Link to={`/machines/${machineId}/conversations/${sourceConversationId}`}>
              Open source conversation <i className="bi bi-arrow-up-right" aria-hidden="true" />
            </Link>
          ) : null}
        </div>
      </div>
    </aside>
  );
}

function resolveEvidence(evidence: PinnedEvidenceDto, turns: ConversationTurnDto[]) {
  const type = normalize(evidence.artifact_type);
  const id = evidence.artifact_id;

  for (const turn of turns) {
    const answer = turn.answer;
    if (!answer) continue;

    const citation = (answer.citations ?? []).find(
      (item) => normalize(item.artifact_type) === type && item.artifact_id === id,
    );
    const artifact = (answer.retrieved_artifacts ?? []).find(
      (item) => normalize(item.artifact_type) === type && item.artifact_id === id,
    );

    if (citation || artifact) {
      return {
        documentId: citation?.document_id ?? artifact?.document_id ?? null,
        label: citation?.label ?? null,
        excerpt: citation?.excerpt ?? null,
        sourceLocator: citation?.source_locator ?? artifact?.source_locator ?? null,
      };
    }
  }

  return {
    documentId: null,
    label: null,
    excerpt: null,
    sourceLocator: null,
  };
}

function normalize(value: string): string {
  return String(value || '').trim().toLocaleUpperCase();
}

function humanize(value: string): string {
  return String(value || '')
    .toLocaleLowerCase()
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (char) => char.toLocaleUpperCase());
}

function locatorText(locator?: Record<string, unknown> | null): string {
  if (!locator) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(locator)) {
    if (value === null || value === undefined || value === '') continue;
    if (typeof value === 'object') continue;
    parts.push(`${humanize(key)}: ${String(value)}`);
  }
  return parts.slice(0, 3).join(' • ');
}

function formatPinnedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return 'to this investigation';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
